import type { Shot, Frame, BreakRecord, SessionStats } from './types';

export function computeSessionStats(
  sessionId: string,
  uid: string,
  date: Date,
  shots: Shot[],
  frames: Frame[],
  durationMinutes: number,
  sessionType: 'match' | 'practice' = 'practice'
): SessionStats {
  const pots       = shots.filter(s => s.type === 'pot');
  const misses     = shots.filter(s => s.type === 'miss');
  const safeties   = shots.filter(s => s.type === 'safety');
  const fouls      = shots.filter(s => s.type === 'foul');
  const longPots   = shots.filter(s => s.type === 'longPot');
  const longMisses = shots.filter(s => s.type === 'longMiss');
  const restPots   = shots.filter(s => s.type === 'restPot');
  const restMisses = shots.filter(s => s.type === 'restMiss');
  const snookered  = shots.filter(s => s.type === 'snookered');

  const totalPots     = pots.length;
  const totalMisses   = misses.length;
  const totalSafeties = safeties.length;
  const totalFouls    = fouls.length;
  const totalShots    = shots.length;

  const goodSafeties      = safeties.filter(s => s.safetyOutcome === 'good').length;
  const safetySuccessRate = totalSafeties > 0
    ? Math.round((goodSafeties / totalSafeties) * 100) : 0;

  const potAttempts = totalPots + totalMisses;
  const potRate     = potAttempts > 0 ? Math.round((totalPots / potAttempts) * 100) : 0;

  const totalLongAttempts = longPots.length + longMisses.length;
  const totalLongPots     = longPots.length;
  const longPotRate       = totalLongAttempts > 0
    ? Math.round((totalLongPots / totalLongAttempts) * 100) : 0;

  const totalRestAttempts = restPots.length + restMisses.length;
  const totalRestPots     = restPots.length;
  const restSuccessRate   = totalRestAttempts > 0
    ? Math.round((totalRestPots / totalRestAttempts) * 100) : 0;

  const snookeredCount    = snookered.length;
  const snookerEscapes    = snookered.filter(s => s.escapeOutcome === 'escaped').length;
  const snookerEscapeRate = snookeredCount > 0
    ? Math.round((snookerEscapes / snookeredCount) * 100) : 0;

  const allBreaks: BreakRecord[] = frames.flatMap(f => f.breaks).filter(b => b.score > 0);
  const breakScores = allBreaks.map(b => b.score);

  const totalBreaks   = breakScores.length;
  const highBreak     = breakScores.length > 0 ? Math.max(...breakScores) : 0;
  const avgBreak      = totalBreaks > 0
    ? Math.round(breakScores.reduce((a, b) => a + b, 0) / totalBreaks) : 0;
  const breaksOver20  = breakScores.filter(b => b >= 20).length;
  const breaksOver50  = breakScores.filter(b => b >= 50).length;
  const breaksOver100 = breakScores.filter(b => b >= 100).length;

  const totalPoints    = pots.reduce((sum, s) => sum + (s.points ?? 0), 0);
  const totalVisits    = totalBreaks + totalMisses + totalSafeties;
  const pointsPerVisit = totalVisits > 0
    ? Math.round((totalPoints / totalVisits) * 10) / 10 : 0;

  const safetyRate = safetySuccessRate;

  return {
    sessionId, uid, date, sessionType,
    totalPots, totalMisses, potRate,
    totalBreaks, highBreak, avgBreak,
    breaksOver20, breaksOver50, breaksOver100, pointsPerVisit,
    totalSafeties, goodSafeties, safetySuccessRate, safetyRate,
    totalFouls,
    snookeredCount, snookerEscapes, snookerEscapeRate,
    totalLongAttempts, totalLongPots, longPotRate,
    totalRestAttempts, totalRestPots, restSuccessRate,
    totalFrames: frames.length,
    totalShots,
    sessionDurationMinutes: durationMinutes,
  };
}

export interface Insight {
  icon: string;
  text: string;
  action: string;
  category: 'potting' | 'breaks' | 'safety' | 'tactical' | 'achievement';
}

export function generateInsights(
  stats: SessionStats,
  prevStats?: SessionStats | null
): Insight[] {
  const pool: Insight[] = [];
  const isMatch = stats.sessionType === 'match';

  // ── Achievements (always surface first) ───────────────────────
  if (stats.highBreak >= 100) {
    pool.push({
      icon: 'trophy', category: 'achievement', action: '',
      text: `Century break of ${stats.highBreak} — a landmark achievement. At this level the run usually ends on position rather than potting — review which ball ended it and why.`,
    });
  } else if (stats.highBreak >= 50) {
    pool.push({
      icon: 'award', category: 'achievement', action: '',
      text: `Break of ${stats.highBreak} this session. Analyse where it ended — if a colour, your positional play off reds needs work. If a red, you left yourself a difficult angle.`,
    });
  }

  if (stats.breaksOver50 >= 2) {
    pool.push({
      icon: 'flame', category: 'achievement', action: '',
      text: `${stats.breaksOver50} breaks of 50+ in a single session — very strong. Consistency across a whole session is harder than one good run.`,
    });
  }

  if (stats.breaksOver20 >= 5) {
    pool.push({
      icon: 'star', category: 'achievement', action: '',
      text: `${stats.breaksOver20} breaks of 20+ today — you're getting in and scoring repeatedly. That's the foundation of a high avg break.`,
    });
  }

  // ── Both stats improved vs last session ───────────────────────
  if (prevStats && stats.potRate > prevStats.potRate && stats.avgBreak > prevStats.avgBreak) {
    pool.push({
      icon: 'chevron-up', category: 'achievement', action: '',
      text: `Pot rate up ${stats.potRate - prevStats.potRate}% and avg break up ${stats.avgBreak - prevStats.avgBreak} from last session — both improving together is the best possible signal.`,
    });
  }

  // ── Potting ───────────────────────────────────────────────────
  if (stats.potRate >= 80) {
    pool.push({
      icon: 'check', category: 'potting',
      action: `Target avg break: ${stats.avgBreak + 8}`,
      text: `${stats.potRate}% pot rate — exceptional. Potting isn't your bottleneck. Every ounce of focus should go on positional play: plan the next red before you cue on the current one.`,
    });
  } else if (stats.potRate >= 65) {
    pool.push({
      icon: 'trending-up', category: 'potting',
      action: 'Try Break Building Drill',
      text: `${stats.potRate}% pot rate — strong. The gap between a 65% and 75% player is mostly about shot selection: the better player attempts fewer low-percentage shots, not more difficult ones.`,
    });
  } else if (stats.potRate >= 50) {
    pool.push({
      icon: 'target', category: 'potting',
      action: 'Try the Line-up Drill',
      text: `${stats.potRate}% pot rate — solid foundation. Focus on eliminating the easy misses: rushed cueing, lifting your head, or jabbing. One fewer unforced miss per frame adds up fast.`,
    });
  } else if (stats.potRate >= 35) {
    pool.push({
      icon: 'target', category: 'potting',
      action: 'Try the Line-up Drill',
      text: `${stats.potRate}% pot rate — you're missing roughly one in two. Slow your backswing right down and pause at the back of the stroke before delivering. Tempo is usually the culprit at this level.`,
    });
  } else {
    pool.push({
      icon: 'target', category: 'potting',
      action: 'Try Baulk Colours Drill',
      text: `${stats.potRate}% pot rate — start short. High-percentage pots from close range build the eye-to-cue-ball-to-object-ball alignment that transfers to longer shots later.`,
    });
  }

  // ── Pot rate trend ─────────────────────────────────────────────
  if (prevStats && Math.abs(stats.potRate - prevStats.potRate) >= 10) {
    const diff = stats.potRate - prevStats.potRate;
    if (diff > 0) {
      pool.push({
        icon: 'trending-up', category: 'potting', action: '',
        text: `Pot rate jumped ${diff}% from last session. Try to identify what felt different — stance, warm-up, pace — so you can replicate it deliberately.`,
      });
    } else {
      pool.push({
        icon: 'trending-down', category: 'potting',
        action: 'Try the Line-up Drill',
        text: `Pot rate dropped ${Math.abs(diff)}% from last session. Small technical regressions — address position, backswing length, bridge stability — are usually the cause. Go back to basics on your next session.`,
      });
    }
  }

  // ── Long potting ───────────────────────────────────────────────
  if (stats.totalLongAttempts >= 3) {
    if (stats.longPotRate >= 60) {
      pool.push({
        icon: 'trending-up', category: 'potting', action: '',
        text: `${stats.longPotRate}% long pot rate (${stats.totalLongPots}/${stats.totalLongAttempts}) — your long game is a genuine scoring weapon. At this level, long pot selection becomes the next skill: knowing which long pots are worth attempting.`,
      });
    } else if (stats.longPotRate >= 35) {
      pool.push({
        icon: 'target', category: 'potting',
        action: 'Try Long Pot Drill',
        text: `${stats.longPotRate}% long pot rate (${stats.totalLongPots}/${stats.totalLongAttempts}). Reasonable, but consider whether some of those misses should have been safeties. A missed long pot often hands your opponent a free visit.`,
      });
    } else if (stats.longPotRate < 35) {
      pool.push({
        icon: 'target', category: 'potting',
        action: 'Try Long Pot Drill',
        text: `${stats.longPotRate}% long pot rate (${stats.totalLongPots}/${stats.totalLongAttempts}) — below 35% means the long is costing you more than it gains. When in doubt, play a safety instead and make your opponent come to you.`,
      });
    }
  } else if (stats.totalLongAttempts === 1 || stats.totalLongAttempts === 2) {
    if (stats.longPotRate === 100) {
      pool.push({
        icon: 'star', category: 'potting', action: '',
        text: `${stats.totalLongPots}/${stats.totalLongAttempts} long pot${stats.totalLongAttempts > 1 ? 's' : ''} — converted every one you attempted. Keep logging long shots to build a meaningful sample.`,
      });
    }
  }

  // ── Rest shots ─────────────────────────────────────────────────
  if (stats.totalRestAttempts >= 3) {
    if (stats.restSuccessRate >= 65) {
      pool.push({
        icon: 'check', category: 'potting', action: '',
        text: `${stats.restSuccessRate}% rest success rate (${stats.totalRestPots}/${stats.totalRestAttempts}) — strong. Comfortable rest technique is a real separator at club and county level.`,
      });
    } else if (stats.restSuccessRate >= 40) {
      pool.push({
        icon: 'lightbulb', category: 'potting', action: '',
        text: `${stats.restSuccessRate}% rest success rate (${stats.totalRestPots}/${stats.totalRestAttempts}). Focus on keeping the rest head absolutely still and following through in a straight line — the most common fault is a sideways jab through contact.`,
      });
    } else {
      pool.push({
        icon: 'lightbulb', category: 'potting', action: '',
        text: `${stats.restSuccessRate}% rest success rate (${stats.totalRestPots}/${stats.totalRestAttempts}) — this is affecting your game. Practise rest shots in isolation: set up easy angles and work purely on a smooth, straight delivery before attempting difficult rest shots in sessions.`,
      });
    }
  }

  // ── Break building ─────────────────────────────────────────────
  if (stats.avgBreak < 8 && stats.totalBreaks >= 3) {
    pool.push({
      icon: 'refresh', category: 'breaks',
      action: 'Try Colours Clearance Drill',
      text: `Avg break of ${stats.avgBreak} across ${stats.totalBreaks} visits — you're leaving the table after 1–2 pots. After each pot, stand up and identify the next red and colour before getting back down. Planning time costs nothing.`,
    });
  } else if (stats.avgBreak >= 8 && stats.avgBreak < 18 && stats.highBreak > stats.avgBreak * 2) {
    pool.push({
      icon: 'zap', category: 'breaks',
      action: 'Try Line-up Drill',
      text: `Best break ${stats.highBreak}, avg break ${stats.avgBreak} — a wide gap means you have the skill but not the consistency. The difference is usually colour selection: are you always taking the colour that leaves you on the easiest next red?`,
    });
  } else if (stats.avgBreak >= 18 && stats.avgBreak < 30) {
    pool.push({
      icon: 'brain', category: 'breaks',
      action: 'Try Break Building Drill',
      text: `Avg break of ${stats.avgBreak} — good territory. Push it higher by planning two shots ahead: know which red you're going to after the colour before you cue on the red. Reactive play caps you at this level.`,
    });
  } else if (stats.avgBreak >= 30) {
    pool.push({
      icon: 'brain', category: 'breaks', action: '',
      text: `Avg break of ${stats.avgBreak} — strong. At this level the black spot becomes your reference point. Every visit, ask: after this colour, can I get back to the black? That single question drives higher scores.`,
    });
  }

  // Points per visit
  if (stats.pointsPerVisit >= 20) {
    pool.push({
      icon: 'lightbulb', category: 'breaks', action: '',
      text: `${stats.pointsPerVisit} points per visit — well above average. Most club players sit at 6–10. This metric reflects both potting skill and smart colour choices.`,
    });
  } else if (stats.pointsPerVisit > 0 && stats.pointsPerVisit < 6 && stats.totalShots >= 15) {
    pool.push({
      icon: 'lightbulb', category: 'breaks',
      action: 'Try Break Building Drill',
      text: `${stats.pointsPerVisit} points per visit. Even taking an easy yellow after every red doubles this quickly. Make it a habit: pot the red, always take a colour — no exceptions.`,
    });
  }

  // ── Snooker escapes ────────────────────────────────────────────
  if (stats.snookeredCount >= 3) {
    if (stats.snookerEscapeRate >= 75) {
      pool.push({
        icon: 'zap', category: 'tactical', action: '',
        text: `${stats.snookerEscapeRate}% escape rate from ${stats.snookeredCount} snookers — excellent defensive resilience. Opponents who snooker you are not gaining the free visits they'd expect.`,
      });
    } else if (stats.snookerEscapeRate >= 50) {
      pool.push({
        icon: 'zap', category: 'tactical',
        action: 'Safety Exchange Drill',
        text: `${stats.snookerEscapeRate}% escape rate (${stats.snookerEscapes}/${stats.snookeredCount} snookers). When you can't escape cleanly, rolling up tight behind a colour rather than attempting a risky escape is often the better play — turn a problem into a counter-snooker.`,
      });
    } else {
      pool.push({
        icon: 'alert', category: 'tactical',
        action: 'Safety Exchange Drill',
        text: `${stats.snookerEscapeRate}% escape rate — you fouled ${stats.snookeredCount - stats.snookerEscapes} of ${stats.snookeredCount} snookers. Practice the thin-contact escape along the cushion: hit the nominated ball as thinly as possible and let the cushion do the work.`,
      });
    }
  } else if (stats.snookeredCount >= 1 && stats.snookerEscapes === 0) {
    pool.push({
      icon: 'alert', category: 'tactical',
      action: 'Safety Exchange Drill',
      text: `Fouled on ${stats.snookeredCount === 1 ? 'the' : 'every'} snooker${stats.snookeredCount > 1 ? ` (${stats.snookeredCount})` : ''}. When snookered, the first aim is always to make contact — even a thin touch that kisses the ball is better than a miss. Use the cushions.`,
    });
  }

  // ── Safety play ────────────────────────────────────────────────
  if (stats.totalSafeties > 0) {
    if (stats.safetySuccessRate >= 75) {
      pool.push({
        icon: 'shield', category: 'safety', action: '',
        text: `${stats.safetySuccessRate}% good safety rate — ${stats.goodSafeties}/${stats.totalSafeties} safeties left your opponent in trouble. Your defensive game is a genuine weapon that wins frames independent of your break-building.`,
      });
    } else if (stats.safetySuccessRate >= 50) {
      pool.push({
        icon: 'shield', category: 'safety',
        action: 'Safety Exchange Drill',
        text: `${stats.safetySuccessRate}% effective safeties. The single biggest upgrade: roll the cue ball close to the bottom cushion on every safety. A ball tight to the cushion requires a much more difficult shot to escape from than one left in the open.`,
      });
    } else if (stats.safetySuccessRate > 0) {
      pool.push({
        icon: 'shield', category: 'safety',
        action: 'Safety Exchange Drill',
        text: `Only ${stats.safetySuccessRate}% of your safeties were effective — poor safeties are more dangerous than no safety at all. When in doubt, play thicker on the red and roll up tight behind a colour on the baulk cushion.`,
      });
    }
  } else if (stats.totalMisses > 5) {
    pool.push({
      icon: 'shield', category: 'tactical',
      action: 'Safety Exchange Drill',
      text: `${stats.totalMisses} misses logged and no safeties. Some of those were almost certainly better played as a safety. The decision tree: is this pot at least 60% likely? If not, play safe.`,
    });
  }

  // ── Match-specific tactical ────────────────────────────────────
  if (isMatch) {
    if (stats.safetySuccessRate >= 65 && stats.totalSafeties >= 4) {
      pool.push({
        icon: 'swords', category: 'tactical', action: '',
        text: `${stats.totalSafeties} safeties at ${stats.safetySuccessRate}% effectiveness in a match — you played a disciplined tactical game. That combination wins frames at any level.`,
      });
    }
    if (stats.longPotRate < 30 && stats.totalLongAttempts >= 3) {
      pool.push({
        icon: 'alert', category: 'tactical', action: '',
        text: `In match play with only ${stats.longPotRate}% long pot success, you're giving away visits by attempting longs. In a tight match, play the safety until you get a shorter opportunity.`,
      });
    }
  }

  // ── Fouls ──────────────────────────────────────────────────────
  if (stats.totalFouls >= 5) {
    pool.push({
      icon: 'alert', category: 'tactical', action: '',
      text: `${stats.totalFouls} fouls — a significant points gift across the session. If these aren't coming from snookers (which you're tracking separately), check for rushed shot selection when under pressure.`,
    });
  } else if (stats.totalFouls >= 3 && stats.snookeredCount < stats.totalFouls) {
    pool.push({
      icon: 'alert', category: 'tactical', action: '',
      text: `${stats.totalFouls} fouls, with fewer logged as snooker escapes — some of these were likely avoidable. Pause before tricky shots and ask if a safety is the better play.`,
    });
  }

  // ── Volume / stamina ───────────────────────────────────────────
  if (stats.totalShots >= 100) {
    pool.push({
      icon: 'activity', category: 'tactical', action: '',
      text: `${stats.totalShots} shots — a high-volume session. Check whether your pot rate held up in the second half, or dropped off. If it dropped, stamina and concentration training are worth adding.`,
    });
  } else if (stats.totalShots >= 60 && stats.sessionDurationMinutes > 0) {
    pool.push({
      icon: 'clock', category: 'tactical', action: '',
      text: `${stats.totalShots} shots in ${stats.sessionDurationMinutes} minutes — a productive session. Regular sessions of this length compound quickly into meaningful skill improvement.`,
    });
  }

  // ── Sort: achievements first, then by most actionable ─────────
  const achievements = pool.filter(i => i.category === 'achievement');
  const rest         = pool.filter(i => i.category !== 'achievement');

  // Within non-achievements, prioritise insights that have a drill action
  const withAction    = rest.filter(i => i.action !== '');
  const withoutAction = rest.filter(i => i.action === '');

  return [...achievements, ...withAction, ...withoutAction].slice(0, 4);
}